import { AfterViewInit, ChangeDetectionStrategy, Component, Injector, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TerminalTransferService } from '@shared/services/export-import-services/terminalTransfer.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { ITerminalTemplate, TerminalTemplateAdministrationService } from '@shared/services/terminaltemplateAdministration.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { CreateTerminalComponent } from '../../mes/terminal-administration/create-terminal/create-terminal.component';
import { DeleteTerminalComponent } from '../../mes/terminal-administration/delete-terminal/delete-terminal.component';
import { EditTerminalComponent } from '../../mes/terminal-administration/edit-terminal/edit-terminal.component';

interface ITableViewModelItem {
	alias: string;
	name: string;
	nextTerminal: string;
	terminal: ITerminal;
	terminalTemplate: string;
}

@Component({
	selector: 'app-terminal-administration',
	templateUrl: './terminal-administration.component.html',
	styleUrls: ['./terminal-administration.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TerminalAdministrationComponent implements OnDestroy, AfterViewInit {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['name', 'nextTerminal', 'terminalTemplate', 'menu'];

	dataSource = new MatTableDataSource<ITableViewModelItem>();
	showSearch = false;
	isLoading = true;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'mes.administration.terminal.export-to-excel',
			click: () => this.onExport(),
			permissions: () => ['mes:TerminalGet', 'mes:WorkTaskGet'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'mes.administration.terminal.import-from-excel',
			click: () => this.onImport(),
			permissions: () => ['mes:TerminalCreate', 'mes:TerminalUpdate', 'mes:TerminalGet', 'mes:WorkTaskGet', 'mes:IncidentCodeGet'],
		},
		{
			icon: () => 'acies_export',
			text: () => 'mes.administration.terminal.download-template',
			// Driven by the transfer service's own capability query rather than a hardcoded
			// per-entity check, so an entity that offers no template loses the entry without a
			// component edit.
			disabled: () => !this.terminalTransferService.hasTemplate(),
			hideOnDisable: () => true,
			click: () => this.downloadTemplate(),
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.terminal.create-terminal',
			click: () => this.openCreateTerminal(),
			permissions: () => ['mes:TerminalCreate', 'mes:TerminalGet', 'mes:WorkTaskGet', 'mes:IncidentCodeGet', 'print:PrinterGet', 'docs:TemplateGetList'],
			disableOnMissingPermissions: () => true,
		},
	];

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private terminalAdministrationService: TerminalAdministrationService,
		private terminalTransferService: TerminalTransferService,
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private router: Router,
		private injector: Injector,
	) {}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	private getTableViewModelItem(terminal: ITerminal, nextTerminal: ITerminal, template: ITerminalTemplate): ITableViewModelItem {
		return {
			alias: terminal.alias,
			name: terminal.name,
			nextTerminal: nextTerminal ? `(${nextTerminal.alias}) ${nextTerminal.name}` : '',
			terminal,
			terminalTemplate: template ? `(${template.alias}) ${template.name}` : '',
		};
	}

	private getTableViewModel(terminals: ITerminal[], templates: ITerminalTemplate[]): ITableViewModelItem[] {
		return terminals.map((terminal) => {
			const nextTerminal: ITerminal = terminals.find((t) => t.id === terminal.nextId);
			const template = templates.find((t) => t.id === terminal.terminalTemplateId);
			return this.getTableViewModelItem(terminal, nextTerminal, template);
		});
	}

	updateList() {
		this.isLoading = true;
		forkJoin({
			terminals: this.terminalAdministrationService.getTerminals({
				includeArriveLeaveTerminals: true,
			}),
			templates: this.terminalTemplateService.getTerminalTemplates(),
		}).subscribe(({ terminals, templates }) => {
			this.dataSource.data = this.getTableViewModel(terminals.result, templates.result);
			this.onSort(this.sort);
			this.isLoading = false;
		});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	openCreateTerminal() {
		const dialogRef = this.dialog.open(CreateTerminalComponent, {
			disableClose: true,
			width: '650px',
			data: null,
		});
		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	openTerminal(terminal: ITerminal | null) {
		void this.router.navigate([`/mes/terminals/${terminal.id}`]);
	}

	openEditTerminal(terminal: ITerminal | null) {
		const dialogRef = this.dialog.open(EditTerminalComponent, {
			disableClose: true,
			width: '650px',
			data: terminal,
		});
		dialogRef.componentInstance.edited.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	openDeleteTerminal(terminal: ITerminal) {
		const dialogRef = this.dialog.open(DeleteTerminalComponent, {
			disableClose: true,
			width: '450px',
			data: terminal,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.notificationService.notify(['mes.administration.terminal.delete.notification.header'], ['mes.administration.terminal.delete.notification.content', { alias: terminal.alias }], 'success');
			this.updateList();
		});
	}

	onExport() {
		// The shared contract exports the caller's in-memory rows rather than re-fetching, the same
		// way DepartmentComponent does. The service still resolves ids to aliases internally.
		const terminals = this.dataSource.data.map((item) => item.terminal);

		this.terminalTransferService
			.exportData(terminals)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.notificationService.notify(['mes.administration.terminal.export.notification.header'], ['mes.administration.terminal.export.notification.content'], 'success');
				},
				error: () => {
					this.notificationService.notify(['mes.administration.terminal.export.error.header'], ['mes.administration.terminal.export.error.content'], 'error');
				},
			});
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.administration.terminal.import.title',
				subHeadingKey: 'mes.administration.terminal.import.subtitle',
				confirmButtonKey: 'mes.administration.terminal.import.button',
				notificationHeader: 'mes.administration.terminal.import.notification.success.header',
				notificationContent: 'mes.administration.terminal.import.notification.success.content',
				notificationErrorHeader: 'mes.administration.terminal.import.notification.error.header',
				notificationErrorContent: 'mes.administration.terminal.import.notification.error.content',
				notificationClientErrorHeader: 'mes.administration.terminal.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.administration.terminal.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.terminalTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'mes.administration.terminal.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		// The refresh used to live in this component's own import subscription. That subscription is
		// now owned by ImportValidatorService, so without this the table would show stale rows after
		// a successful import.
		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.updateList();
			});
	}

	downloadTemplate() {
		this.terminalTransferService.getTemplate();
		this.notificationService.notify(['mes.administration.terminal.template.notification.header'], ['mes.administration.terminal.template.notification.content'], 'success');
	}
}
